import React from 'react';
import { arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import { getGenericRow } from 'ps-components/AdvancedTable/GenericGrid/GenericGridFunctions';
import { getTransformedColumns } from 'ps-components/AdvancedTable/GenericGrid/GenericGridFunctions';
import AdvancedTable from 'ps-components/AdvancedTable/AdvancedTable';
import CellIndicator from 'ps-components/LoadingIndicator/CellIndicator/CellIndicator';
import cloneDeep from 'lodash/cloneDeep';
import cn from 'classnames';

import './ReviewImpactGrid.scss';

ReviewImpactGrid.propTypes = {
  employeeData: object,
  history: object,
  jobCodeKeyText: string,
  loading: bool,
  loadMoreEmployees: func,
  locationData: object,
  locationType: string,
  numericRounding: number,
  onSortChange: func,
  percentRounding: number,
  reviewImpactGrid: object,
  reviewImpactGridActions: object,
  rowMetadata: arrayOf(
    shape({
      count: number,
      expanded: bool,
      loading: bool,
      location: string,
      noMore: bool
    })
  ),
  toggleLocation: func,
  useJobCodeKey: bool
};

function ReviewImpactGrid(props) {
  if (!props.locationData) {
    return null;
  }

  const columns = getTransformedColumns(
    props.locationData.termGroups,
    props.locationData.terms,
    props.locationData.data,
    null,
    null
  );

  // For a given location, return all the generic grid data from employeeData (terms, groups, data),
  // but with the data filtered to only correct employees
  // also, check if row is expanded here
  function getSubRowData(el) {
    let location;
    if (props.locationType === 'workLocation') {
      location = el?.values?.work_location_default;
    } else {
      location = el?.values?.city_state_default;
    }

    if (!location) {
      return null;
    }

    const isExpanded = props.rowMetadata?.[location]?.expanded;

    if (isExpanded) {
      const subRowData = cloneDeep(props.employeeData);

      let employeeLocationIndex;
      if (props.locationType === 'workLocation') {
        employeeLocationIndex = subRowData?.terms?.findIndex(t => t.term === 'work_location_default');
      } else {
        employeeLocationIndex = subRowData?.terms?.findIndex(t => t.term === 'city_state_default');
      }

      subRowData.data = subRowData?.data?.filter(e => e[employeeLocationIndex] === location);

      return subRowData;
    }

    return null;
  }

  // return all employee rows from employeeData that have the same location as rowData
  // i.e. get all the employee sub rows for a given location main row
  function getSubRows(rowData) {
    let isLocationRow = false;
    const firstItem = rowData?.[0];
    if (typeof firstItem === 'string' && firstItem?.includes('(') && firstItem?.includes(')')) {
      isLocationRow = true;
    }
    if (!isLocationRow) {
      return [];
    }

    let locationRowIndex;
    let employeeLocationIndex;
    if (props.locationType === 'workLocation') {
      locationRowIndex = props.locationData?.terms?.findIndex(t => t.term === 'work_location_default');
      employeeLocationIndex = props.employeeData?.terms?.findIndex(t => t.term === 'work_location_default');
    } else {
      locationRowIndex = props.locationData?.terms?.findIndex(t => t.term === 'city_state_default');
      employeeLocationIndex = props.employeeData?.terms?.findIndex(t => t.term === 'city_state_default');
    }

    const location = rowData[locationRowIndex];

    if (!location) {
      return [];
    }

    const employeeRows = props.employeeData?.data?.filter(e => e[employeeLocationIndex] === location);

    return employeeRows;
  }

  function getRowIcon(data) {
    const location = data?.values?.work_location_default || data?.values?.city_state_default;
    if (location && props.rowMetadata) {
      if (location === 'All Locations') {
        return <img className="review-impact__all-locations-icon" src="/view/static/mp/images/location_icon.svg" />;
      } else {
        const isExpanded = props.rowMetadata[location]?.expanded;

        return (
          <div
            className={cn('review-impact__toggle', { collapsed: !isExpanded })}
            onClick={() => props.toggleLocation(location)}
          />
        );
      }
    }

    return null;
  }

  function getShowMoreEmployees(mainRow) {
    // If the number of sub rows is less than 3, that means there aren't any more to load
    const subRowCount = mainRow?.subRows?.length;

    if (subRowCount < 3) {
      return null;
    }

    let location;
    if (props.locationType === 'workLocation') {
      location = mainRow?.values?.work_location_default;
    } else {
      location = mainRow?.values?.city_state_default;
    }

    const rowMetadata = props.rowMetadata[location];
    const isLoading = rowMetadata?.loading;
    const currentCount = rowMetadata?.count;
    const noMore = rowMetadata?.noMore;

    if (noMore) {
      return null;
    }

    return (
      <div className="review-impact__sub-row-load-more">
        {isLoading ? (
          <CellIndicator iconClass="icon-animate-spin icon-spin cell-indicator" />
        ) : (
          <div onClick={() => props.loadMoreEmployees(location, currentCount)}>Show more employees</div>
        )}
      </div>
    );
  }

  function getNumberOfDecimals(key, value) {
    const geoDiffIndex = props.employeeData?.terms?.findIndex(t => t.term === 'geo_differential_official');
    if (geoDiffIndex > -1 && key === 'field_geo_differential_official' && value && value !== '-') {
      const split = value?.toString()?.split('.');
      let numberOfDecimals = 0;
      if (split.length > 1) {
        numberOfDecimals = split[1]?.length;
      }

      return numberOfDecimals;
    }

    return props.numericRounding;
  }

  return (
    <div className="generic-grid__container review-impact__grid-container">
      <AdvancedTable
        {...props.reviewImpactGrid.tableProps}
        {...props.reviewImpactGridActions}
        getDataRow={(row, idx) => {
          return getGenericRow(row, idx, {
            excludeLoadingText: true,
            jobCodeKeyText: props.jobCodeKeyText,
            getNumberOfDecimals: (key, value) => getNumberOfDecimals(key, value),
            onClick: null,
            percentRounding: props.percentRounding,
            useJobCodeKey: props.useJobCodeKey
          });
        }}
        allowResize={true}
        allowSorting={true}
        columnHeaders={columns}
        dataLoading={props.loading}
        getRowIcon={getRowIcon}
        getShowMoreLink={getShowMoreEmployees}
        getSubRowData={getSubRowData}
        getSubRows={getSubRows}
        loadingText={'Loading Location and Employee Data'}
        onSortChange={props.onSortChange}
        rowData={props.locationData.data}
        stickyHeader
        totalCount={props.locationData.data?.length}
        useClientSideSort={false}
        useSelectAll={false}
      />
    </div>
  );
}

export default ReviewImpactGrid;
